import { useCallback } from "react";
import dayjs from "dayjs";
import { Button, Checkbox, DatePicker, Input, Segmented, Typography } from "antd";
import { SELECTION_CONSENT_CLAUSES } from "../../utils/deliveryConsent";
import { useTranslation } from "../../i18n";

const { Paragraph } = Typography;

const PRESET_DAYS = [7, 14, 30, 90];

// 选片链接表单字段（生成页与管理编辑共用，独立于 delivery）。
// values: { albumPath, albumName, consentClauses(number[]), expiry(dayjs) }
function SelectionFormFields({ values, onChange, showPathHint = true }) {
    const { t } = useTranslation();

    const disabledDate = useCallback((current) => {
        return current && current < dayjs().startOf("day");
    }, []);

    const disabledDateTime = useCallback((current) => {
        if (!current || !current.isSame(dayjs(), "day")) {
            return {};
        }
        const now = dayjs();
        return {
            disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
            disabledMinutes: (hour) => {
                if (hour > now.hour()) {
                    return [];
                }
                if (hour < now.hour()) {
                    return Array.from({ length: 60 }, (_, i) => i);
                }
                return Array.from({ length: now.minute() }, (_, i) => i);
            },
            disabledSeconds: (hour, minute) => {
                if (hour > now.hour() || minute > now.minute()) {
                    return [];
                }
                if (hour < now.hour() || minute < now.minute()) {
                    return Array.from({ length: 60 }, (_, i) => i);
                }
                return Array.from({ length: now.second() + 1 }, (_, i) => i);
            },
        };
    }, []);

    const applyPreset = useCallback(
        (days) => {
            onChange(
                "expiry",
                dayjs().add(days, "day").hour(23).minute(59).second(59).millisecond(0)
            );
        },
        [onChange]
    );

    return (
        <>
            <div className="delivery-admin-field">
                <label htmlFor="selection-album-name">{t("selection.admin.albumNameLabel")}</label>
                <Input
                    id="selection-album-name"
                    placeholder={t("selection.admin.albumNamePlaceholder")}
                    value={values.albumName}
                    onChange={(e) => onChange("albumName", e.target.value)}
                />
                <Paragraph
                    type="secondary"
                    style={{ marginTop: "0.5rem", marginBottom: 0, fontSize: "0.85rem" }}
                >
                    {t("selection.admin.albumNameHint")}
                </Paragraph>
            </div>

            <div className="delivery-admin-field">
                <label htmlFor="selection-album-path">{t("selection.admin.pathLabel")}</label>
                <Input
                    id="selection-album-path"
                    placeholder={t("selection.admin.pathPlaceholder")}
                    value={values.albumPath}
                    onChange={(e) => onChange("albumPath", e.target.value)}
                />
                {showPathHint && (
                    <Paragraph
                        type="secondary"
                        style={{ marginTop: "0.5rem", marginBottom: 0, fontSize: "0.85rem" }}
                    >
                        {t("selection.admin.pathHint")}
                    </Paragraph>
                )}
            </div>

            <div className="delivery-admin-field">
                <label>{t("selection.admin.expiryLabel")}</label>
                <div className="delivery-admin-presets">
                    {PRESET_DAYS.map((days) => (
                        <Button key={days} onClick={() => applyPreset(days)}>
                            {t("common.days", { count: days })}
                        </Button>
                    ))}
                </div>
                <DatePicker
                    showTime={{ format: "HH:mm:ss" }}
                    format="YYYY-MM-DD HH:mm:ss"
                    value={values.expiry}
                    onChange={(value) => value && onChange("expiry", value)}
                    disabledDate={disabledDate}
                    disabledTime={disabledDateTime}
                    style={{ width: "100%" }}
                    inputReadOnly
                />
            </div>

            <div className="delivery-admin-field">
                <label htmlFor="selection-archive-markdown">
                    {t("selection.admin.archiveMarkdownLabel")}
                </label>
                <Input.TextArea
                    id="selection-archive-markdown"
                    placeholder={t("selection.admin.archiveMarkdownPlaceholder")}
                    value={values.archiveMarkdown}
                    onChange={(e) => onChange("archiveMarkdown", e.target.value)}
                    autoSize={{ minRows: 4, maxRows: 14 }}
                />
                <Paragraph
                    type="secondary"
                    style={{ marginTop: "0.5rem", marginBottom: 0, fontSize: "0.85rem" }}
                >
                    {t("selection.admin.archiveMarkdownHint")}
                </Paragraph>
            </div>

            <div className="delivery-admin-field">
                <label>{t("selection.admin.consentLabel")}</label>
                <Checkbox.Group
                    className="delivery-admin-consent-group"
                    value={values.consentClauses || []}
                    onChange={(value) => onChange("consentClauses", value)}
                >
                    {SELECTION_CONSENT_CLAUSES.map((clause) => (
                        <Checkbox key={clause.id} value={clause.id}>
                            {clause.kind === "social"
                                ? t("selection.consent.clauses.socialLabel")
                                : t(`selection.consent.clauses.${clause.key}`)}
                        </Checkbox>
                    ))}
                </Checkbox.Group>
                <Paragraph
                    type="secondary"
                    style={{ marginTop: "0.5rem", marginBottom: 0, fontSize: "0.85rem" }}
                >
                    {t("selection.admin.consentHint")}
                </Paragraph>
            </div>

            <div className="delivery-admin-field">
                <label>{t("selection.admin.downloadAllLabel")}</label>
                <Segmented
                    block
                    value={values.downloadAllType}
                    onChange={(value) => onChange("downloadAllType", value)}
                    options={[
                        { label: t("selection.admin.downloadAllNone"), value: "none" },
                        { label: t("selection.admin.downloadAllZip"), value: "zip" },
                        { label: t("selection.admin.downloadAllGdrive"), value: "gdrive" },
                    ]}
                />
                {values.downloadAllType !== "none" && (
                    <Input
                        style={{ marginTop: "0.75rem" }}
                        placeholder={
                            values.downloadAllType === "zip"
                                ? t("selection.admin.downloadAllZipPlaceholder")
                                : t("selection.admin.downloadAllGdrivePlaceholder")
                        }
                        value={values.downloadAllToken}
                        onChange={(e) => onChange("downloadAllToken", e.target.value)}
                    />
                )}
                <Paragraph
                    type="secondary"
                    style={{ marginTop: "0.5rem", marginBottom: 0, fontSize: "0.85rem" }}
                >
                    {t("selection.admin.downloadAllHint")}
                </Paragraph>
            </div>
        </>
    );
}

export default SelectionFormFields;
